import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { TextField, TextFieldVariants } from "@mui/material";
import type { Dayjs } from "dayjs";
import { CSSProperties, useState } from "react";

interface TimeClockPickerProps {
  label?: string;
  disabled?: boolean;
  value: Dayjs | string;
  onChange: (value: Dayjs) => void;
  minTime?: Dayjs;
  maxTime?: Dayjs;
  hoursOnly?: boolean;
  minutesStep?: number;
  variant?: TextFieldVariants;
  inputStyle?: CSSProperties;
  allowMidnightAsEndOfDay?: boolean;
}

export const TimeClockPicker = ({
  label = "",
  disabled = false,
  value,
  onChange,
  minTime,
  maxTime,
  hoursOnly,
  variant = "standard",
  inputStyle,
  allowMidnightAsEndOfDay = false,
}: TimeClockPickerProps) => {
  const [inProgressHour, setInProgressHour] = useState<number | null>(null);

  const shouldDisableTime = allowMidnightAsEndOfDay && minTime
    ? (timeValue: number, clockType: string) => {
        if (clockType === "hours") {
          // always allow midnight (00:00 = end of day)
          if (timeValue === 0) return false;
          return timeValue < minTime.hour();
        }
        if (clockType === "minutes") {
          const currentHour = inProgressHour ?? (value as Dayjs).hour?.();
          // midnight hour: only 00:00 is valid (00:30 disabled)
          if (currentHour === 0) return timeValue !== 0;
          // same hour as minTime: only allow minutes >= minTime.minute()
          if (currentHour === minTime.hour()) return timeValue < minTime.minute();
        }
        return false;
      }
    : undefined;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <TimePicker
        minutesStep={30}
        views={hoursOnly ? ["hours"] : ["hours", "minutes"]}
        ampm={false}
        label={label}
        disabled={disabled}
        value={value}
        onAccept={(newValue) => {
          setInProgressHour(null);
          onChange(newValue as Dayjs);
        }}
        onChange={(newValue) => {
          if (newValue) setInProgressHour((newValue as Dayjs).hour?.() ?? null);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            variant={variant}
            error={false}
            fullWidth
            style={inputStyle}
          />
        )}
        minTime={allowMidnightAsEndOfDay ? undefined : minTime}
        maxTime={maxTime}
        shouldDisableTime={shouldDisableTime}
      />
    </LocalizationProvider>
  );
};
